import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bible-reference-picker',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, TitleCasePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BibleReferencePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BibleReferencePickerComponent),
      multi: true,
    },
  ],
  templateUrl: './bible-reference-picker.component.html',
})
export class BibleReferencePickerComponent<T extends Record<string, string>>
  implements ControlValueAccessor, OnInit, Validator, OnDestroy
{
  // @ViewChild('chapterInput') chapterInput!: ElementRef;
  @Input() bibleEnum!: Record<string, string>;
  @Input() label: string = 'Select Book';
  @Input() placeholder: string = 'e.g. Genesis';
  @Output() onSelect = new EventEmitter<string>();
  @Input() verseRequired: boolean = true;

  innerForm: FormGroup;

  query: string = '';
  filteredBooks: string[] = [];
  showDropdown = false;
  activeIndex: number = -1;

  exit$: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder) {
    this.innerForm = this.fb.group({
      book: ['', [Validators.required]],
      chapter: [null, [Validators.required, Validators.min(1)]],
      verse: [null],
    });
  }

  ngOnInit() {
    this.updateVerseValidators();

    this.innerForm.valueChanges
      .pipe(takeUntil(this.exit$))
      .subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      });
  }

  private updateVerseValidators() {
    const verseControl = this.innerForm.get('verse');
    if (this.verseRequired) {
      verseControl?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      verseControl?.clearValidators();
    }
    // Refresh the control status
    verseControl?.updateValueAndValidity({ emitEvent: false });
  }

  // --- ControlValueAccessor ---
  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: any): void {
    if (value) {
      this.innerForm.patchValue(value, { emitEvent: false });
    } else {
      this.innerForm.reset(
        { book: '', chapter: null, verse: null },
        { emitEvent: false },
      );
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.innerForm.disable() : this.innerForm.enable();
  }

  // --- Validator Interface ---
  // This allows the parent form to know if the internal fields are invalid
  validate(control: AbstractControl): ValidationErrors | null {
    return this.innerForm.valid ? null : { incompleteReference: true };
  }

  ngOnDestroy() {
    this.exit$.next();
    this.exit$.complete();
  }

  onBookInput() {
    const value = this.innerForm.get('book')?.value?.toLowerCase() || '';
    if (!value) {
      this.filteredBooks = [];
      this.showDropdown = false;
      this.activeIndex = -1; // Reset
      return;
    }

    this.filteredBooks = Object.values(this.bibleEnum).filter((book) =>
      book.toLowerCase().includes(value),
    );

    this.showDropdown = true;
    this.activeIndex = 0; // Highlight the first item by default
  }

  handleBookKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown || this.filteredBooks.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault(); // Stop cursor from moving
        this.activeIndex = (this.activeIndex + 1) % this.filteredBooks.length;
        break;

      case 'ArrowUp':
        event.preventDefault(); // Stop cursor from moving
        this.activeIndex =
          (this.activeIndex - 1 + this.filteredBooks.length) %
          this.filteredBooks.length;
        break;

      case 'Enter':
      case 'Tab':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.selectBook(this.filteredBooks[this.activeIndex].valueOf());
        }
        break;

      case 'Escape':
        this.showDropdown = false;
        this.activeIndex = -1;
        break;
    }
  }

  selectBook(book: string) {
    this.innerForm.patchValue({ book: book.toLowerCase() });
    this.showDropdown = false;
    this.filteredBooks = [];
    this.activeIndex = -1; // Reset for next time
  }

  hideDropdown() {
    // Timeout allows the click event on the list to fire before it's removed from DOM
    setTimeout(() => (this.showDropdown = false), 200);
  }
}
